/**
 * Module Automatisations — modèle partagé avec l'API (copie de `services/automations/model.ts`),
 * appels réseau et opérations PURES sur le scénario (lot 2c, 2026-10-28).
 * Toute règle métier reste côté serveur (« Vérifier ») ; ici seulement l'ergonomie.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api';

export type AutomationScope = 'all_posts' | 'post' | 'story_reply' | 'dm' | 'link';
export type MatchMode = 'exact' | 'contains' | 'word';
export type AudienceMode = 'none' | 'invite' | 'require_follow';
export type AutomationStatus = 'draft' | 'active' | 'paused' | 'error';

export type ScenarioButton = { kind: 'url'; title: string; url: string } | { kind: 'choice'; title: string };
export interface FirstMessage { text: string; text_variations: string[]; buttons_version: { text: string; buttons: ScenarioButton[] } | null }

export type NodeType = 'trigger' | 'message' | 'question' | 'wait' | 'condition' | 'action' | 'delay';
export interface MessageNodeData { text: string; variations?: string[]; media?: { type: 'image' | 'video'; url: string } | null; buttons?: ScenarioButton[]; quick_replies?: { title: string }[] }
export interface QuestionNodeData { text: string; save_as: string; validation: 'phone' | 'email' | 'text' | 'choice'; choices?: string[]; retry_text?: string }
export interface WaitNodeData { timeout_minutes: number }
export interface ConditionRule { field: string; op: 'equals' | 'contains' | 'exists' | 'has_tag'; value?: string }
export interface ConditionNodeData { rules: ConditionRule[]; mode?: 'all' | 'any' }
export type ActionNodeData =
  | { type: 'add_tag'; tag: string }
  | { type: 'set_field'; field: string; value: string }
  | { type: 'notify_telegram'; message?: string }
  | { type: 'send_lead' }
  | { type: 'handoff'; note?: string }
  | { type: 'end' };
export interface DelayNodeData { minutes: number }
export type NodeData = MessageNodeData | QuestionNodeData | WaitNodeData | ConditionNodeData | ActionNodeData | DelayNodeData | Record<string, never>;
export interface ScenarioNode { id: string; type: NodeType; data: NodeData; position?: { x: number; y: number } }
export interface ScenarioEdge { id?: string; source: string; target: string; sourceHandle?: string | null }
export interface ScenarioGraph { nodes: ScenarioNode[]; edges: ScenarioEdge[] }

export interface AutomationInput {
  name: string;
  publishing_account_id: string;
  scope: AutomationScope;
  platform_post_id?: string | null;
  provider_post_id?: string | null;
  keywords: string[];
  match_mode: MatchMode;
  exclude_keywords: string[];
  public_replies: string[];
  dm_delay_seconds: number;
  also_match_in_dms: boolean;
  audience_mode: AudienceMode;
  first_message: FirstMessage;
  scenario: ScenarioGraph;
}

export interface AutomationRecord extends AutomationInput {
  id: string;
  status: AutomationStatus;
  provider_automation_id: string | null;
  version: number;
  template_source: string | null;
  created_at: string;
  updated_at: string;
  account: { id: string; platform: string; username: string | null; provider_account_id: string; status: string } | null;
}

export interface VerifyIssue { code: string; severity: 'error' | 'warning'; message: string; node_id?: string }
export interface AccountPost { platformPostId: string; providerPostId: string | null; permalink: string | null; caption: string | null; publishedAt: string | null; thumbnailUrl: string | null }
export interface TemplateBody extends Partial<Omit<AutomationInput, 'first_message' | 'scenario' | 'publishing_account_id'>> { first_message: FirstMessage; scenario: ScenarioGraph }
export interface TemplateDetail { key: string; audience: 'mr' | 'mars' | 'all'; name: Record<string, string>; description: Record<string, string>; product_key: string | null; body: TemplateBody }
export interface TestRun { id: string; status: string; current_node_id: string | null; waiting_for: string; started_at: string; completed_at: string | null; ended_reason: string | null; vars: Record<string, unknown>; participant_username: string | null }
export interface TestEvent { kind: string; node_id: string | null; at: string; data: Record<string, unknown> }

/** Mêmes plafonds que `verify.ts` côté serveur (affichage des compteurs ; le serveur reste juge). */
export const LIMITS = { textWithButtons: 640, textPlain: 1000, buttonTitle: 20, buttons: 3, quickReplies: 13, variations: 5, publicReplies: 6, keywordMin: 3, keywords: 20, waitMaxMinutes: 1440 } as const;

/** Toutes les portées sont servies (lot 5 : naissance sur message privé, story et lien) ; `SCOPES_SOON` reste pour masquer une portée au besoin. */
export const SCOPES_AVAILABLE: AutomationScope[] = ['all_posts', 'post', 'story_reply', 'dm', 'link'];
export const SCOPES_SOON: AutomationScope[] = [];

/** Lien à partager pour la portée « lien » (ig.me / m.me) : le code est le premier mot-clé. */
export function shareLink(platform: string | null | undefined, username: string | null | undefined, keyword: string | undefined): string | null {
  const code = (keyword ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-|-$/g, '');
  if (!username || !code) return null;
  if (platform === 'instagram') return `https://ig.me/m/${username}?ref=${code}`;
  if (platform === 'facebook') return `https://m.me/${username}?ref=${code}`;
  return null;
}

// ─── Lot 4 : fiches produits, « Rédiger avec l'IA » · Lot 5 : statistiques ──

export interface ProductCtas { demo_line?: string; booking_url?: string; booking_label?: string; source_code?: string }
export interface Product { id: string; name: string; promise: string | null; capabilities: string[]; forbidden: string[]; ctas: ProductCtas; keywords: string[]; faq: Array<{ q: string; a: string }>; ai_disclosure: string | null; sectors: string[]; validated_at: string | null; seed_key: string | null; created_at: string; updated_at: string }
export type ProductInput = Omit<Product, 'id' | 'validated_at' | 'seed_key' | 'created_at' | 'updated_at'>;

export function useProducts() {
  return useQuery({ queryKey: ['automations', 'products'], queryFn: () => apiRequest<{ products: Product[] }>('/automations/products'), staleTime: 60_000 });
}
export function useSaveProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: ProductInput }) => (id ? apiRequest<{ product: Product }>(`/automations/products/${id}`, { method: 'PUT', body: input }) : apiRequest<{ product: Product }>('/automations/products', { method: 'POST', body: input })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['automations', 'products'] }),
  });
}
export function useProductAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action, validated }: { id: string; action: 'validate' | 'delete'; validated?: boolean }) => {
      if (action === 'delete') { await apiRequest<void>(`/automations/products/${id}`, { method: 'DELETE' }); return null; }
      return apiRequest<{ product: Product }>(`/automations/products/${id}/validate`, { method: 'POST', body: { validated: validated ?? true } });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['automations', 'products'] }),
  });
}

export interface DraftFlag { where: string; sentence: string; status: 'covered' | 'neutral' | 'unknown' | 'forbidden'; reason?: string }
export interface DraftResult { first_message: FirstMessage; public_replies: string[]; scenario: ScenarioGraph; keywords: string[]; audience_mode: AudienceMode; control: { flags: DraftFlag[]; summary: Record<'covered' | 'neutral' | 'unknown' | 'forbidden', number>; disclosure_missing: boolean }; product: { id: string; name: string; validated: boolean }; model: string }
export function useGenerateDraft() {
  return useMutation({ mutationFn: (input: { product_id: string; caption: string; lang: string; sectors?: string[] }) => apiRequest<DraftResult>('/automations/draft', { method: 'POST', body: input }) });
}
/** Même découpage que le contrôle serveur, pour surligner les phrases du brouillon. */
export function splitSentences(text: string): string[] {
  return text.split(/(?<=[^\d][.!?…])\s+|\n+/).map((s) => s.trim()).filter((s) => s.length > 0);
}

export interface StatsFunnel { triggered: number; first_sent: number; reply: number; choice: number; button: number; callback_requested: number; handoff: number; lead_ready: number; lead_sent: number; stop: number; completed: number; human_sent: number; error: number }
export interface StatsOverview {
  days: number;
  automations: Array<{ id: string; name: string; status: AutomationStatus; scope: AutomationScope; platform: string | null; username: string | null; platform_post_id: string | null; funnel: StatsFunnel }>;
  by_network: Array<{ platform: string; triggered: number; first_sent: number; reply: number; lead_ready: number }>;
  by_post: Array<{ platform_post_id: string; name: string; platform: string | null; triggered: number; reply: number; lead_ready: number }>;
  leads: Array<{ at: string; prospect_id: string; name: string | null; handle: string | null; platform: string | null; sector: string | null; phone: string | null; template_name: string | null; conversation_id: string | null }>;
  daily: Array<{ day: string; triggered: number; first_sent: number; reply: number; lead_ready: number }>;
}
export function useStatsOverview(days: number) {
  return useQuery({ queryKey: ['automations', 'stats', days], queryFn: () => apiRequest<StatsOverview>(`/automations/stats/overview?days=${days}`), staleTime: 60_000 });
}

// ─── Appels ──────────────────────────────────────────────────────────────────

export const automationKey = (id: string) => ['automations', 'one', id] as const;

export function useAutomation(id: string | null) {
  return useQuery({ queryKey: automationKey(id ?? 'none'), queryFn: () => apiRequest<AutomationRecord>(`/automations/${id}`), enabled: !!id, staleTime: 10_000 });
}

export function useSaveAutomation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: AutomationInput }) =>
      id ? apiRequest<AutomationRecord>(`/automations/${id}`, { method: 'PUT', body: input }) : apiRequest<AutomationRecord>('/automations', { method: 'POST', body: input }),
    onSuccess: (rec) => { qc.setQueryData(automationKey(rec.id), rec); void qc.invalidateQueries({ queryKey: ['automations'], exact: true }); },
  });
}

export function useAutomationAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'activate' | 'pause' | 'duplicate' | 'delete' }) => {
      if (action === 'delete') { await apiRequest<void>(`/automations/${id}`, { method: 'DELETE' }); return null; }
      return apiRequest<AutomationRecord>(`/automations/${id}/${action}`, { method: 'POST' });
    },
    onSuccess: (rec, { id, action }) => {
      if (rec && action !== 'duplicate') qc.setQueryData(automationKey(id), rec);
      void qc.invalidateQueries({ queryKey: ['automations'], exact: true });
    },
  });
}

export function useVerifyAutomation() {
  return useMutation({ mutationFn: (id: string) => apiRequest<{ ok: boolean; issues: VerifyIssue[] }>(`/automations/${id}/verify`, { method: 'POST' }) });
}

export function useAccountPosts(accountId: string | null, enabled: boolean) {
  return useQuery({ queryKey: ['automations', 'posts', accountId], queryFn: () => apiRequest<{ posts: AccountPost[] }>(`/automations/accounts/${accountId}/posts`), enabled: !!accountId && enabled, staleTime: 60_000 });
}

export function useTemplateDetail(key: string | null) {
  return useQuery({ queryKey: ['automations', 'template', key], queryFn: () => apiRequest<{ template: TemplateDetail }>(`/automations/templates/${key}`), enabled: !!key, staleTime: 5 * 60_000 });
}

export function useAutomationsAccess(enabled = true) {
  return useQuery({ queryKey: ['automations', 'access'], queryFn: () => apiRequest<{ enabled: boolean }>('/automations/access'), staleTime: 5 * 60_000, retry: 0, enabled });
}

export function useStartTest() {
  return useMutation({ mutationFn: ({ id, version }: { id: string; version: 'text' | 'buttons' }) => apiRequest<{ run_id: string }>(`/automations/${id}/test`, { method: 'POST', body: { version } }) });
}

export function useTestStatus(id: string | null, live: boolean) {
  return useQuery({ queryKey: ['automations', 'test', id], queryFn: () => apiRequest<{ run: TestRun | null; events: TestEvent[] }>(`/automations/${id}/test`), enabled: !!id, refetchInterval: live ? 3000 : false });
}

// ─── Scénario : opérations pures ─────────────────────────────────────────────

export type Handle = string;

/** Sorties affichées par l'éditeur simple (mêmes noms que `expectedHandles` côté serveur). */
export function branchesOf(node: ScenarioNode): Handle[] {
  switch (node.type) {
    case 'message': {
      const d = node.data as MessageNodeData;
      const n = (d.buttons ?? []).filter((b) => b.kind === 'choice').length || (d.quick_replies ?? []).length;
      return n ? [...Array.from({ length: n }, (_, i) => `choice:${i}`), 'reply'] : ['default'];
    }
    case 'question': {
      const d = node.data as QuestionNodeData;
      return d.validation === 'choice' ? [...Array.from({ length: d.choices?.length ?? 0 }, (_, i) => `choice:${i}`), 'invalid'] : ['answer', 'invalid'];
    }
    case 'wait': return ['reply', 'timeout'];
    case 'condition': return ['yes', 'no'];
    case 'action': { const t = (node.data as ActionNodeData).type; return t === 'end' || t === 'handoff' ? [] : ['default']; }
    case 'trigger':
    case 'delay': return ['default'];
    default: return [];
  }
}

/** Titres des choix numérotés d'un nœud (boutons « choix », réponses rapides ou choix d'une question). */
export function choiceTitles(node: ScenarioNode | undefined): string[] {
  if (!node) return [];
  if (node.type === 'question') { const d = node.data as QuestionNodeData; return d.validation === 'choice' ? (d.choices ?? []) : []; }
  if (node.type === 'message') {
    const d = node.data as MessageNodeData;
    const c = (d.buttons ?? []).filter((b): b is { kind: 'choice'; title: string } => b.kind === 'choice').map((b) => b.title);
    return c.length ? c : (d.quick_replies ?? []).map((q) => q.title);
  }
  return [];
}

export const nodeById = (g: ScenarioGraph, id: string | null | undefined) => (id ? g.nodes.find((n) => n.id === id) : undefined);
export const edgeFrom = (g: ScenarioGraph, source: string, handle: Handle) => g.edges.find((e) => e.source === source && (e.sourceHandle ?? 'default') === handle);
export const firstStepId = (g: ScenarioGraph): string | null => { const trig = g.nodes.find((n) => n.type === 'trigger'); return trig ? edgeFrom(g, trig.id, 'default')?.target ?? null : null; };

let seq = 0;
export const newNodeId = (type: NodeType) => `${type}_${Date.now().toString(36)}${(seq++).toString(36)}`;

export function defaultData(type: NodeType): NodeData {
  switch (type) {
    case 'message': return { text: '', buttons: [] };
    case 'question': return { text: '', save_as: 'text', validation: 'text', choices: [], retry_text: '' };
    case 'wait': return { timeout_minutes: 1200 };
    case 'condition': return { rules: [{ field: 'sector', op: 'exists' }], mode: 'all' };
    case 'action': return { type: 'notify_telegram', message: '' };
    case 'delay': return { minutes: 60 };
    default: return {};
  }
}

export const emptyScenario = (): ScenarioGraph => ({ nodes: [{ id: 'trigger', type: 'trigger', data: {} }], edges: [] });

/** Retire les nœuds que le déclencheur n'atteint plus (le serveur refuse les orphelins). */
export function prune(g: ScenarioGraph): ScenarioGraph {
  const trig = g.nodes.find((n) => n.type === 'trigger');
  if (!trig) return g;
  const seen = new Set<string>([trig.id]);
  const stack = [trig.id];
  while (stack.length) {
    const cur = stack.pop()!;
    for (const e of g.edges) if (e.source === cur && !seen.has(e.target)) { seen.add(e.target); stack.push(e.target); }
  }
  return { nodes: g.nodes.filter((n) => seen.has(n.id)), edges: g.edges.filter((e) => seen.has(e.source) && seen.has(e.target)) };
}

/** Ajoute une étape à la sortie `handle` de `source` ; une suite existante est raccordée à la première sortie de la nouvelle étape. */
export function addStep(g: ScenarioGraph, source: string, handle: Handle, type: NodeType): { graph: ScenarioGraph; id: string } {
  const id = newNodeId(type);
  const node: ScenarioNode = { id, type, data: defaultData(type) };
  const existing = edgeFrom(g, source, handle);
  const edges = g.edges.filter((e) => e !== existing);
  edges.push({ source, target: id, sourceHandle: handle === 'default' ? null : handle });
  if (existing) {
    const first = branchesOf(node)[0];
    if (first) edges.push({ source: id, target: existing.target, sourceHandle: first === 'default' ? null : first });
  }
  return { graph: prune({ nodes: [...g.nodes, node], edges }), id };
}

/** Supprime une étape : avec une seule suite, elle est raccordée au parent ; sinon tout ce qui suit disparaît. */
export function removeStep(g: ScenarioGraph, id: string): ScenarioGraph {
  const out = g.edges.filter((e) => e.source === id);
  const incoming = g.edges.filter((e) => e.target === id);
  let edges = g.edges.filter((e) => e.source !== id && e.target !== id);
  if (out.length === 1) edges = [...edges, ...incoming.map((e) => ({ ...e, target: out[0]!.target }))];
  return prune({ nodes: g.nodes.filter((n) => n.id !== id), edges });
}

export function updateStep(g: ScenarioGraph, id: string, data: NodeData): ScenarioGraph {
  const nodes = g.nodes.map((n) => (n.id === id ? { ...n, data } : n));
  const node = nodes.find((n) => n.id === id)!;
  const allowed = new Set(branchesOf(node));
  return prune({ nodes, edges: g.edges.filter((e) => e.source !== id || allowed.has(e.sourceHandle ?? 'default')) });
}

/** Version texte d'un message à choix : « Répondez 1, 2 ou 3 » + choix numérotés (même forme que le serveur). */
export function numberedChoices(choices: string[]): string {
  return choices.map((c, i) => `${i + 1}. ${c}`).join('\n');
}
export function replyInstruction(count: number, lang: string): string {
  const nums = Array.from({ length: count }, (_, i) => String(i + 1));
  const list = nums.length > 1 ? `${nums.slice(0, -1).join(', ')} ${lang.startsWith('en') ? 'or' : lang.startsWith('nl') ? 'of' : 'ou'} ${nums[nums.length - 1]}` : nums[0] ?? '';
  return lang.startsWith('en') ? `Reply ${list}.` : lang.startsWith('nl') ? `Antwoord ${list}.` : `Répondez ${list}.`;
}

export const platformLabel: Record<string, string> = { instagram: 'Instagram', facebook: 'Facebook', linkedin: 'LinkedIn', tiktok: 'TikTok', youtube: 'YouTube', threads: 'Threads' };
